import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ChatText, ArrowUpRight, ArrowLeft, Users, PaperPlaneRight, CheckCircle } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import api, { formatApiError } from "@/lib/api";

const BETA_URL = "https://www.gruvygraphicdesign.com/beatbox";

export default function Contact() {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");

  async function onSubmit(e) {
    e.preventDefault();
    if (!name.trim() || !email.trim() || !message.trim()) return;
    setSending(true);
    setError("");
    try {
      await api.post("/feedback", { name: name.trim(), email: email.trim(), message: message.trim() });
      setSent(true);
    } catch (err) {
      setError(formatApiError(err?.response?.data?.detail) || "Couldn't send — please try again.");
    } finally {
      setSending(false);
    }
  }

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white flex flex-col" data-testid="contact-page">
      {/* Header — mirrors Landing's marquee-style navigation */}
      <header className="border-b border-white/10">
        <div className="max-w-[1500px] mx-auto px-6 lg:px-12 py-6 flex items-center justify-between">
          <Link
            to="/"
            data-testid="contact-back-home"
            className="inline-flex items-center gap-2 font-mono uppercase text-xs tracking-widest text-white/60 hover:text-[#A8FF1F] transition-colors"
          >
            <ArrowLeft size={14} weight="bold" /> BACK
          </Link>
          <Link to="/" className="font-display text-2xl tracking-tighter">BEAT BOX</Link>
          <span className="font-mono uppercase text-xs tracking-widest text-white/40 hidden md:inline">CONTACT</span>
        </div>
      </header>

      <main className="flex-1">
        <section className="max-w-[1500px] mx-auto px-6 lg:px-12 py-16 lg:py-24">
          {/* Editorial page title */}
          <div className="mb-14 lg:mb-20 max-w-3xl">
            <span className="inline-block border border-[#A8FF1F]/40 bg-[#A8FF1F]/10 text-[#A8FF1F] font-mono uppercase text-xs tracking-widest rounded-full px-3 py-1">
              GET IN TOUCH
            </span>
            <h1 className="font-display text-5xl md:text-6xl lg:text-7xl tracking-tighter leading-[0.95] mt-6">
              Say hi, or<br />join the beta.
            </h1>
            <p className="text-white/70 text-base lg:text-lg mt-6 max-w-xl leading-relaxed">
              Two ways to reach us — drop your feedback directly, or sign up as an early beta tester and help shape where Beat Box goes next.
            </p>
          </div>

          {/* Two-card grid — matches the two-column feature blocks on Landing */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Feedback form card — dark with lime icon */}
            <div
              data-testid="contact-feedback-card"
              className="bb-card relative overflow-hidden flex flex-col p-8 lg:p-10 bg-[#0F1013] border-white/10 min-h-[280px]"
            >
              <div className="w-14 h-14 rounded-full bg-[#A8FF1F] flex items-center justify-center mb-6 shadow-[0_6px_20px_rgba(168,255,31,0.35)]">
                <ChatText size={26} weight="fill" className="text-[#0A0A0A]" />
              </div>
              <span className="font-mono uppercase text-xs tracking-widest text-white/50 mb-2">SEND FEEDBACK</span>
              {sent ? (
                <div className="flex-1 flex flex-col justify-center items-start" data-testid="contact-feedback-success">
                  <CheckCircle size={40} weight="fill" className="text-[#A8FF1F] mb-4" />
                  <h2 className="font-display text-3xl tracking-tighter mb-2">Got it — thank you.</h2>
                  <p className="text-white/60 text-sm leading-relaxed">
                    Your feedback landed. We read everything and it genuinely shapes what we build next.
                  </p>
                </div>
              ) : (
                <>
                  <h2 className="font-display text-3xl md:text-4xl tracking-tighter mb-3">
                    Tell us what you think.
                  </h2>
                  <p className="text-white/60 text-sm leading-relaxed mb-6">
                    Questions, ideas, bugs, partnership pitches — it all goes here and straight to the team.
                  </p>
                  <form onSubmit={onSubmit} className="flex flex-col gap-3">
                    <Input
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      placeholder="Your name"
                      required
                      maxLength={120}
                      data-testid="contact-feedback-name"
                      className="bg-[#16171A] border-white/10 rounded-xl h-11 text-white placeholder:text-white/30 focus-visible:ring-[#A8FF1F]"
                    />
                    <Input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="Account email"
                      required
                      data-testid="contact-feedback-email"
                      className="bg-[#16171A] border-white/10 rounded-xl h-11 text-white placeholder:text-white/30 focus-visible:ring-[#A8FF1F]"
                    />
                    <Textarea
                      value={message}
                      onChange={(e) => setMessage(e.target.value)}
                      placeholder="Your message or feedback…"
                      required
                      rows={4}
                      maxLength={4000}
                      data-testid="contact-feedback-message"
                      className="bg-[#16171A] border-white/10 rounded-xl text-white placeholder:text-white/30 resize-none focus-visible:ring-[#A8FF1F]"
                    />
                    {error && (
                      <p className="text-[#F87171] text-xs font-mono" data-testid="contact-feedback-error">{error}</p>
                    )}
                    <Button
                      type="submit"
                      disabled={sending || !name.trim() || !email.trim() || !message.trim()}
                      data-testid="contact-feedback-submit"
                      className="bb-pill mt-1 self-start bg-[#A8FF1F] hover:bg-[#94E600] text-[#0A0A0A] h-11 px-6 text-sm font-semibold disabled:opacity-40"
                    >
                      {sending ? "Sending…" : "Send feedback"} <PaperPlaneRight className="ml-2" size={14} weight="fill" />
                    </Button>
                  </form>
                </>
              )}
            </div>

            {/* Beta tester card — inverted lime background for pop */}
            <a
              href={BETA_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="contact-beta-card"
              className="bb-card group relative overflow-hidden flex flex-col p-8 lg:p-10 bg-[#A8FF1F] text-[#0A0A0A] border-[#A8FF1F] hover:shadow-[0_10px_40px_rgba(168,255,31,0.35)] transition-shadow min-h-[280px]"
            >
              <div className="w-14 h-14 rounded-full bg-[#0A0A0A] flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                <Users size={26} weight="fill" className="text-[#A8FF1F]" />
              </div>
              <span className="font-mono uppercase text-xs tracking-widest text-[#0A0A0A]/60 mb-2">BETA TESTERS</span>
              <h2 className="font-display text-3xl md:text-4xl tracking-tighter mb-3">
                Help build Beat Box.
              </h2>
              <p className="text-[#0A0A0A]/75 text-sm leading-relaxed">
                Sign up to try new features before anyone else. You&rsquo;ll get early access, direct line to the team, and a permanent mention when we ship.
              </p>
              <div className="mt-auto pt-6 flex items-center gap-3">
                <span
                  data-testid="contact-beta-link"
                  className="inline-flex items-center gap-2 bg-[#0A0A0A] text-white font-mono uppercase text-xs tracking-widest px-4 py-2.5 rounded-full group-hover:bg-[#1A1B1D] transition-colors"
                >
                  Sign up as a tester
                  <ArrowUpRight size={14} weight="bold" />
                </span>
              </div>
            </a>
          </div>

          {/* Secondary CTA back to landing */}
          <div className="mt-16 text-center">
            <Link to="/">
              <Button
                data-testid="contact-cta-home"
                variant="ghost"
                className="bb-pill border-2 border-[#A8FF1F] hover:bg-[#A8FF1F]/10 text-white h-12 px-6 text-sm font-semibold"
              >
                Back to Beat Box <ArrowUpRight className="ml-2" size={16} weight="bold" />
              </Button>
            </Link>
          </div>
        </section>
      </main>

      <footer className="border-t border-white/10">
        <div className="max-w-[1500px] mx-auto px-6 lg:px-12 py-8 flex flex-col md:flex-row items-start md:items-center justify-between gap-3">
          <div className="font-display text-xl tracking-tighter">BEAT BOX</div>
          <div className="text-white/40 text-xs font-mono uppercase tracking-widest">
            © 2026 Gruvy Graphics LLC
          </div>
        </div>
      </footer>
    </div>
  );
}
